/**
 * Records WHICH STOREFRONT the customer is buying on, as cart attributes that
 * carry over to the order (order.attributes / note_attributes / customAttributes).
 *
 * Why this exists: order.customer_locale is the BUYER's locale (browser or
 * account language), so it happily reports en_US or de_NL on a French Belgian
 * order. It is not the language Shopify actually rendered. The values written
 * here come from Liquid -- request.locale.iso_code and localization.* -- which
 * are server-side facts about the page that was served.
 *
 * The market alone is not enough: the belgium market publishes nl, fr AND de,
 * so market and language have to be recorded as separate keys.
 *
 * `domain` is recorded too, and is the value to trust when the others look
 * surprising. Market handles are not consistently named on this store (the
 * Belgium market's handle is 'belgium' but Germany's is 'de'), so `market`
 * alone cannot tell you which storefront someone was on. request.host can.
 *
 * Last-write-wins on every page view, not just at add-to-cart: if someone fills
 * a cart in nl and switches to fr before checking out, the order should say fr.
 * The dataset guard means this only costs a request when the cart is actually
 * out of date -- once per session, plus once per language switch.
 */
class LocaleCartAttributes extends HTMLElement {
  // Shopify normally persists attributes written to an empty cart, but if it
  // ever discards them the cartUpdate re-assert below would retry forever.
  static MAX_WRITES = 3;

  connectedCallback() {
    this.writeCount = 0;

    const { market, country, language, domain } = this.dataset;
    this.desired = { market, country, language, domain };

    // Rendered by Liquid from the cart as it stood when this page was built.
    const onCart = {
      market: this.dataset.cartMarket,
      country: this.dataset.cartCountry,
      language: this.dataset.cartLanguage,
      domain: this.dataset.cartDomain,
    };

    if (this.isStale(onCart)) this.schedule();

    // Backstop: another component may have created the cart after this page was
    // rendered (first add-to-cart from an empty cart), in which case the Liquid
    // snapshot above was taken before the cart existed.
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      if (data && data.cart && this.isStale(data.cart.attributes || {})) this.schedule();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  isStale(attributes) {
    return Object.keys(this.desired).some((key) => (attributes[key] || '') !== this.desired[key]);
  }

  schedule() {
    if (this.pending || this.writeCount >= LocaleCartAttributes.MAX_WRITES) return;
    this.pending = true;

    const write = this.write.bind(this);
    'requestIdleCallback' in window ? requestIdleCallback(write, { timeout: 3000 }) : setTimeout(write, 0);
  }

  write() {
    this.writeCount += 1;

    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body: JSON.stringify({ attributes: this.desired }) },
    })
      .catch((error) => {
        console.error('Error writing locale cart attributes:', error);
      })
      .finally(() => {
        this.pending = false;
      });
  }
}
customElements.define('locale-cart-attributes', LocaleCartAttributes);
